import React, { useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { DisclaimerBanner } from "@/components/DisclaimerBanner";
import { useLang } from "@/contexts/LanguageContext";

function emiOf(amount, ratePct, tenureMonths) {
  if (!amount || !ratePct || !tenureMonths) return 0;
  const r = ratePct / 100 / 12;
  return Math.round((amount * r * Math.pow(1 + r, tenureMonths)) / (Math.pow(1 + r, tenureMonths) - 1));
}

export default function OfferComparison() {
  const [items, setItems] = useState([]);
  const { t, tv } = useLang();
  useEffect(() => { api.get("/borrower/lender-interests").then((r) => setItems(r.data)).catch(() => {}); }, []);
  const posted = items.filter((i) => i.status === "posted" || i.status === "accepted");

  const L = t("oc_rows");
  const effort = (i) => (i.additional_documents?.length > 3 ? t("high") : i.additional_documents?.length > 1 ? t("medium") : t("low"));
  const speed = (i) => (i.expected_response_days <= 7 ? t("fast") : i.expected_response_days <= 14 ? t("medium") : t("slow"));
  const fns = [
    (i) => i.lender_name,
    (i) => `${formatINR(i.indicative_amount_min)} – ${formatINR(i.indicative_amount_max)}`,
    (i) => `${i.indicative_rate_min}% – ${i.indicative_rate_max}%`,
    (i) => tv(i.rate_type),
    (i) => formatINR(emiOf((i.indicative_amount_min + i.indicative_amount_max) / 2, (i.indicative_rate_min + i.indicative_rate_max) / 2, i.indicative_tenure_months)),
    (i) => `${i.indicative_tenure_months} ${t("months")}`,
    (i) => `${i.indicative_moratorium_months} ${t("months")}`,
    (i) => `${i.processing_fee_pct}%`,
    (i) => tv(i.collateral_expectation),
    (i) => (i.personal_guarantee ? t("yes") : t("no")),
    (i) => tv(i.cgtmse_consideration),
    (i) => (i.additional_documents || []).map(tv).join(", ") || "—",
    (i) => tv(i.branch) || "—",
    (i) => `${i.expected_response_days} ${t("days")}`,
    (i) => `${i.offer_validity_days} ${t("days")}`,
    effort,
    speed,
  ];
  const rows = fns.map((fn, k) => [L[k], fn, k]);

  return (
    <div className="space-y-6" data-testid="offer-compare-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 brand-heading">{t("oc_title")}</h1>
        <p className="text-slate-600 mt-1 text-sm">{t("oc_sub")}</p>
      </div>
      {posted.length === 0 ? (
        <div className="p-6 rounded-lg border border-dashed border-slate-300 bg-slate-50 text-slate-600 text-sm">{t("oc_none")}</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="text-left p-3 text-xs uppercase tracking-widest text-slate-500 bg-slate-50 border-b border-slate-200 sticky left-0">{t("oc_attr")}</th>
                {posted.map((i) => (
                  <th key={i.id} className="text-left p-3 text-xs uppercase tracking-widest text-slate-500 bg-slate-50 border-b border-slate-200 min-w-[200px]">{i.lender_name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(([label, fn, idx]) => (
                <tr key={idx} className={idx % 2 ? "bg-slate-50" : ""} data-testid={`cmp-row-${idx}`}>
                  <td className="p-3 font-medium text-slate-800 sticky left-0 bg-inherit">{label}</td>
                  {posted.map((i) => <td key={i.id} className="p-3 text-slate-900">{fn(i)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-xs text-amber-900">
        {t("oc_note")}
      </div>
      <DisclaimerBanner compact />
    </div>
  );
}
